/**
 * iter660 — Epson POS printer model registry.
 *
 * Curated from Epson's spec sheets. Used by the iPad Quick-Add flow to
 * surface capability badges (AirPrint / Bluetooth / Network / USB)
 * the moment a cashier picks their model — so they instantly know
 * whether they can print from Safari or need a Bridge.
 *
 * `airprint`: whether the model ships AirPrint-capable firmware.
 * `firmware_toggle`: true when AirPrint is OFF by default and must be
 *                    enabled via the printer's web admin (Network →
 *                    AirPrint → Enable). False = always-on once on LAN.
 * `bt`/`net`/`usb`: physical interfaces the unit exposes.
 * `aliases`: substrings we look for inside the printer's HTTP <title>
 *            or SNMP sysDescr when auto-detecting.
 */

export const PRINTER_MODELS = [
  {
    id: 'tm-m30iii',
    label: 'Epson TM-m30III',
    airprint: true, firmware_toggle: false, bt: true, net: true, usb: true,
    aliases: ['TM-m30III', 'TM-M30III'],
    note: 'Best iPad fit. AirPrint on by default.',
  },
  {
    id: 'tm-m30ii-nt',
    label: 'Epson TM-m30II-NT (Ethernet + USB)',
    airprint: true, firmware_toggle: true, bt: false, net: true, usb: true,
    aliases: ['TM-m30II-NT', 'TM-M30II-NT'],
    note: 'AirPrint must be enabled in the printer\u2019s web UI.',
  },
  {
    id: 'tm-m30ii',
    label: 'Epson TM-m30II (Wi-Fi + Bluetooth)',
    airprint: true, firmware_toggle: true, bt: true, net: true, usb: true,
    aliases: ['TM-m30II', 'TM-M30II'],
    note: 'AirPrint must be enabled in the printer\u2019s web UI.',
  },
  {
    id: 'tm-m30ii-bt',
    label: 'Epson TM-m30II-BT (Bluetooth only)',
    airprint: false, firmware_toggle: false, bt: true, net: false, usb: true,
    aliases: ['TM-m30II-BT', 'TM-M30II-BT'],
    note: 'Bluetooth-only \u2192 cannot AirPrint. Use OS Bluetooth pairing on the iPad, or a Lantronix bridge.',
  },
  {
    id: 'tm-m30',
    label: 'Epson TM-m30 (older)',
    airprint: false, firmware_toggle: false, bt: true, net: true, usb: true,
    aliases: ['TM-m30', 'TM-M30'],
    note: 'Older model. No AirPrint firmware \u2192 needs a PC Bridge.',
  },
  {
    id: 'tm-t88vi-ihub',
    label: 'Epson TM-T88VI-iHub',
    airprint: true, firmware_toggle: false, bt: false, net: true, usb: true,
    aliases: ['TM-T88VI-iHub', 'TM-T88VI-IHUB'],
    note: 'AirPrint-ready out of the box.',
  },
  {
    id: 'tm-t88vi',
    label: 'Epson TM-T88VI',
    airprint: true, firmware_toggle: true, bt: false, net: true, usb: true,
    aliases: ['TM-T88VI', 'TM-T88VII'],
    note: 'AirPrint must be enabled in the printer\u2019s web UI.',
  },
  {
    id: 'tm-t20iii',
    label: 'Epson TM-T20III',
    airprint: false, firmware_toggle: false, bt: false, net: false, usb: true,
    aliases: ['TM-T20III', 'TM-T20', 'TM-T20II'],
    note: 'USB only. Connect to a PC running the Bridge.',
  },
  {
    id: 'sunmi-nt310',
    label: 'Sunmi NT310 / NT211',
    airprint: false, firmware_toggle: false, bt: true, net: true, usb: true,
    aliases: ['NT310', 'NT211', 'SUNMI'],
    note: 'Sunmi clones do not implement AirPrint. Use a PC Bridge or Lantronix.',
  },
  {
    id: 'other',
    label: 'Other / unknown',
    airprint: null, firmware_toggle: false, bt: null, net: null, usb: null,
    aliases: [],
    note: 'Pick this if your printer isn\u2019t listed. Compatibility will depend on its manual.',
  },
];

export function findModelById(id) {
  return PRINTER_MODELS.find((m) => m.id === id) || null;
}

/**
 * Match a probed identity string (HTTP <title> or SNMP sysDescr) to one
 * of the registered models. Case-insensitive. First match wins.
 */
export function findModelByIdentity(identity) {
  if (!identity) return null;
  const hay = String(identity).toLowerCase();
  for (const m of PRINTER_MODELS) {
    for (const a of m.aliases) {
      if (hay.includes(a.toLowerCase())) return m;
    }
  }
  return null;
}
